import React, { useEffect, useState } from "react";
import "./App.css";
import {
  FaEnvelope,
  FaGithub,
  FaGlobe,
  FaLinkedin,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { FiExternalLink, FiMoon, FiSun } from "react-icons/fi";

const profileImage =
  "https://res.cloudinary.com/dxetyokin/image/upload/v1671288686/Assigment%201/Intro_Picture_Me_pciuao.jpg";

const stackItems = ["React", "TypeScript", "JavaScript", "Node.js", "MongoDB"];

const links = [
  {
    title: "GitHub",
    subtitle: "github.com/peterpapas",
    href: "https://github.com/peterpapas",
    icon: FaGithub,
  },
  {
    title: "LinkedIn",
    subtitle: "Let's connect professionally",
    href: "https://www.linkedin.com/in/panagiotis-papanastasatos/",
    icon: FaLinkedin,
  },
  {
    title: "Portfolio",
    subtitle: "fullstackpeter.com",
    href: "https://fullstackpeter.com/",
    icon: FaGlobe,
  },
  {
    title: "Email",
    subtitle: "panagiotis.1230@hotmail.com",
    href: "mailto:panagiotis.1230@hotmail.com",
    icon: FaEnvelope,
  },
];

function getInitialTheme() {
  try {
    return localStorage.getItem("pp-theme") || "dark";
  } catch {
    return "dark";
  }
}

function getMelbourneTime() {
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Melbourne",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(new Date());
  } catch {
    return "";
  }
}

function LinkCard({ href, icon: Icon, subtitle, title }) {
  const isExternal = href.startsWith("http");

  return (
    <a
      className="link-card"
      href={href}
      rel={isExternal ? "noopener noreferrer" : undefined}
      target={isExternal ? "_blank" : undefined}
    >
      <span className="link-card__icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="link-card__copy">
        <span className="link-card__title">{title}</span>
        <span className="link-card__subtitle">{subtitle}</span>
      </span>
      <span className="link-card__arrow" aria-hidden="true">
        <FiExternalLink />
      </span>
    </a>
  );
}

function SectionLabel({ children, id }) {
  return (
    <div className="section-label">
      <span className="section-label__line" aria-hidden="true" />
      <h2 id={id}>{children}</h2>
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [melbourneTime, setMelbourneTime] = useState(getMelbourneTime);

  useEffect(() => {
    try {
      localStorage.setItem("pp-theme", theme);
    } catch {
      // Theme persistence is a progressive enhancement.
    }
  }, [theme]);

  useEffect(() => {
    const timer = setInterval(() => {
      setMelbourneTime(getMelbourneTime());
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  };

  return (
    <main className="page-shell" data-theme={theme}>
      <article className="profile-card" aria-labelledby="profile-name">
        <header className="profile-header">
          <div className="profile-toolbar">
            <div className="availability-badge">
              <span className="availability-badge__dot" aria-hidden="true" />
              <span>Available for work</span>
            </div>
            <button
              aria-label={`Switch to ${
                theme === "dark" ? "light" : "dark"
              } theme`}
              aria-pressed={theme === "dark"}
              className="theme-toggle"
              onClick={toggleTheme}
              type="button"
            >
              {theme === "dark" ? (
                <FiMoon aria-hidden="true" />
              ) : (
                <FiSun aria-hidden="true" />
              )}
            </button>
          </div>

          <div className="profile-identity">
            <img
              alt="Peter Papas sitting by the Yarra River in Melbourne at sunset"
              className="profile-avatar"
              height="114"
              src={profileImage}
              width="114"
            />
            <h1 id="profile-name">Peter Papas</h1>
            <p className="profile-role">Full-Stack Software Engineer</p>
            <p className="profile-location">
              <FaMapMarkerAlt aria-hidden="true" />
              <span>Melbourne, Australia</span>
            </p>
          </div>
        </header>

        <div className="profile-divider" />

        <section
          className="content-section about-section"
          aria-labelledby="about-heading"
        >
          <SectionLabel id="about-heading">About</SectionLabel>
          <div className="about-copy">
            <p>
              Hey there! I'm Peter <span aria-hidden="true">👋</span>, a{" "}
              <strong>Full-Stack Software Engineer</strong> based in Melbourne,
              Australia.
            </p>
            <p>
              For the past 3 years at <strong>Mentorloop</strong>, I've
              contributed to the development of a large-scale SaaS platform,
              delivering features across the front end, back end, integrations,
              testing, and deployment workflows.
            </p>
            <p>
              My experience spans React, JavaScript, TypeScript, Node.js,
              MongoDB, and modern web technologies, with a strong focus on
              product quality and user experience.
            </p>
            <p>
              Before Mentorloop, I worked as a Front-End Developer at{" "}
              <strong>Nintendo Australia</strong>, and I continue to bring that
              product-minded attention to detail into the software I build
              today.
            </p>
            <p>
              I'm passionate about software engineering, AI-powered tools, and
              building products that solve real-world problems.
            </p>
          </div>
        </section>

        <section
          className="content-section stack-section"
          aria-labelledby="stack-heading"
        >
          <SectionLabel id="stack-heading">Stack</SectionLabel>
          <ul className="stack-list" aria-label="Technology stack">
            {stackItems.map((item) => (
              <li className="stack-pill" key={item}>
                <span aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section
          className="content-section links-section"
          aria-labelledby="links-heading"
        >
          <SectionLabel id="links-heading">Links</SectionLabel>
          <div className="link-list">
            {links.map((link) => (
              <LinkCard key={link.title} {...link} />
            ))}
          </div>
        </section>

        <footer className="profile-footer">
          <p className="footer-location">
            <span aria-hidden="true" />
            {melbourneTime
              ? `Melbourne · ${melbourneTime}`
              : "Melbourne, Australia"}
          </p>
          <p>© 2026 Peter Papas · Designed &amp; built by keyborad</p>
        </footer>
      </article>
    </main>
  );
}

export default App;
